import { civilFromInstant, instantFromCivil } from '@/components/ui/date-picker/date-utils';
import { reportingTodayInput, zonedTodayInput } from '@/lib/report-timezone';

export interface ReportRange {
  dateFrom: string;
  dateTo: string;
}

/** Default range for reports whose pickers include an optional time-of-day
 *  (datetime-local): today, full day (00:00 → 23:59). The reporting-zone
 *  conversion pads the 23:59 end to :59.999 so it stays a full day. */
export function defaultDailyRangeWithTime(tz?: string): ReportRange {
  const today = tz ? zonedTodayInput(tz) : reportingTodayInput();
  return { dateFrom: `${today}T00:00`, dateTo: `${today}T23:59` };
}

/**
 * Range carried in on a deep link (`&from=…&to=…`), e.g. from the home page's
 * "View Details" links. Without this a drill-down would open on the report's own
 * default of *today* rather than the period the user was just looking at, which
 * makes the link actively misleading — same report, different numbers.
 *
 * The format matches the wall-clock strings the reports already exchange, and is
 * validated rather than trusted: a hand-edited URL falls back to the default
 * instead of feeding a malformed range into the query.
 */
export function deepLinkRange(): ReportRange | null {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const q = hash.indexOf('?');
  if (q < 0) return null;
  const params = new URLSearchParams(hash.slice(q + 1));
  const from = params.get('from');
  const to = params.get('to');
  const wellFormed = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v);
  if (!wellFormed(from) || !wellFormed(to) || from > to) return null;
  return { dateFrom: from, dateTo: to };
}

/**
 * The range a report opens on: the deep link's if it carried one, otherwise the
 * report's own default. Only consulted on mount — Reset still returns the report
 * to today rather than back to wherever the link came from.
 */
export function initialDailyRange(tz?: string): ReportRange {
  return deepLinkRange() ?? defaultDailyRangeWithTime(tz);
}

// ── Bridge between the report's wall-clock `YYYY-MM-DDTHH:mm` strings (which the
// query layer interprets in the reporting zone, see withLocalDateRange) and the
// Date instants the reusable <DatePicker> exchanges. Both sides use the same
// reporting zone, so a value round-trips without drifting. ──────────────────────
export function dailyInputToDate(value: string, tz: string): Date | null {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart ?? '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return instantFromCivil({ year: y, month: m, day: d, hour: hh || 0, minute: mm || 0, second: 0 }, tz);
}

export function dateToDailyInput(date: Date | null, tz: string): string {
  if (!date) return '';
  const c = civilFromInstant(date, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}T${p(c.hour)}:${p(c.minute)}`;
}
